import { createSessionStore } from "@/app/activity/store"
import Radio from "@/components/form/radio/Radio"
import { Session } from "@/types/session"

import { MouseEvent, useEffect, useMemo, useRef } from "react"
import { format } from "date-fns"
import { useTranslation } from "react-i18next"

import "./sessionListItem.scss"
import BasicButton from "@/components/button/BasicButton"
import classNames from "classnames"
import { HOUR_TO_SECOND_FORMAT } from "@/constants/regex"

export default function SessionListItem({
  session,
  onClick,
  onCopyClick,
}: {
  session: Session
  onClick: (session: Session) => void
  onCopyClick?: (session: Session) => void
}) {
  const { t } = useTranslation()
  const {
    selectedSession,
    activeIdfSession,
    setUpsertDialogOpen,
    hoveredSession,
    setConfirmSaveDialogOpen,
    temporarySession,
    setSession,
    setHoveredSession,
    setTemporarilySession,
  } = createSessionStore((state) => ({
    selectedSession: state.session,
    setSessions: state.setSessions,
    setUpsertDialogOpen: state.setUpsertDialogOpen,
    activeIdfSession: state.activeIdfSession,
    setConfirmSaveDialogOpen: state.setConfirmSaveDialogOpen,
    temporarySession: state.temporarySession,
    setSession: state.setSession,
    setHoveredSession: state.setHoveredSession,
    hoveredSession: state.hoveredSession,
    setTemporarilySession: state.setTemporarySession,
  }))

  const sessionDuration = useMemo(() => {
    const { dateFinish, dateStart } = session
    if (dateFinish && dateStart) {
      return `${format(new Date(dateStart), HOUR_TO_SECOND_FORMAT)} ~ ${format(new Date(dateFinish), HOUR_TO_SECOND_FORMAT)}`
    }
  }, [session])

  const itemRef = useRef<HTMLDivElement>(null)

  const isSelected = useMemo(
    () => selectedSession?.idfSession === session.idfSession,
    [activeIdfSession, session.idfSession, selectedSession],
  )

  useEffect(() => {
    if (isSelected && itemRef.current) {
      itemRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" })
    }
  }, [isSelected])

  const onClickHandler = () => {
    onClick(session)
  }

  const onEditClick = () => {
    setSession(session)
    setUpsertDialogOpen(true)
  }

  const onActionClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()

    if (!isSubset(temporarySession as Session, selectedSession as Session)) {
      setConfirmSaveDialogOpen(true)
    } else {
      setSession(undefined)
    }
  }

  const onMouseOverHandler = () => {
    setHoveredSession(session)
  }

  const onMouseLeaveHandler = () => {
    setHoveredSession(undefined)
  }

  const isSubset = (a: Record<string, unknown>, b: Record<string, unknown>): boolean => {
    return Object.keys(b).every((key) => {
      if (typeof b[key] === "object" && b[key] !== null) {
        return isSubset(a[key] as Record<string, unknown>, b[key] as Record<string, unknown>)
      }

      if (Array.isArray(b[key])) {
        return b[key].every((item: unknown) => (a[key] as unknown[]).includes(item))
      }

      return a[key] === b[key]
    })
  }

  return (
    <div
      ref={itemRef}
      className={classNames("SessionListItemCont", {
        selected: isSelected,
        hovered: !isSelected && hoveredSession?.idfSession === session.idfSession,
      })}
      onClick={onClickHandler}
      onMouseEnter={onMouseOverHandler}
      onMouseLeave={onMouseLeaveHandler}>
      <Radio checked={isSelected} />
      <p className="session-title">{session.name}</p>
      <div className="spacer" />
      <div className="session-item">
        <p>{session.type}</p>
        <p>{sessionDuration}</p>
        <p>
          {session.playerCount} {t("player")}
        </p>
      </div>

      {isSelected && (
        <>
          <div className="spacer" />
          <div className="session-action">
            <BasicButton
              type="default-outlined"
              size="xs"
              onClick={(e) => {
                e.stopPropagation()
                onCopyClick?.(session)
              }}>
              {t("button.copy")}
            </BasicButton>
            <BasicButton
              type="default-outlined"
              size="xs"
              onClick={(e) => {
                e.stopPropagation()
                onEditClick()
              }}>
              {t("button.edit")}
            </BasicButton>
            <BasicButton type="primary-outlined" size="xs" onClick={onActionClick}>
              {t("button.done")}
            </BasicButton>
          </div>
        </>
      )}
    </div>
  )
}
